import React, { Fragment } from "react";
import {ApiCalls, getVariation, setDeviceTags, setIdUploadStatus, trackOptimizelyEvent} from "../../constants/ApiCalls";
import { DocType } from "../../constants/DocType";
import { ApiErrors } from "../../constants/Errors";
import { FlowStates } from "../../constants/FlowStates";
import { Logger } from "../../constants/Logger";
import { PubSubEvents } from "../../constants/PubSubEvents";
import { Routes } from "../../constants/Routing";
import { Marbles } from "../../screens/Marbles";
import { Camera, CameraMode } from "../camera/Camera";
import { CapturedImage, CapturedImageResult, FourK, SevenTwenty } from "../camera/Video";
import { CaptureInstructions } from "./CaptureInstructions";
import "./DocCapture.css";
import {ImageReview} from "./ImageReview";
import {ManualInstructions} from "./ManualInstructions";
import {SuccessfulCapture} from "./SuccessfulCapture";
import {
  DOC_CAPTURE_EXPERIMENT_CONTROL,
  DOC_CAPTURE_EXPERIMENT_VARIATION,
  DOC_CAPTURE_LOCAL_DECODE_SUCCESS_EVENT,
  DOC_CAPTURE_MANUAL_CAMERA_EXPERIMENT,
  DOC_CAPTURE_SUCCESS_EVENT,
} from "../../constants/Experiments";
import {IDVMethod} from "../../constants/IDVMethod";
import {IdUploadStatus} from "../../constants/IdUploadStatus";

export interface DocCaptureProps {
  appId?: string;
  debug: boolean;
  documents: DocType[];
  uuid?: string;
  coBorrower: string | null;
  enableLocalDecode: boolean;
  docType?: string;
}

export interface DocCaptureState {
  backendAttempts?: number;
  capturedImage?: CapturedImage;
  docIndex: number;
  enableLocalDecode: boolean;
  firstUpload: boolean;
  view: DLViewEnum;
  manualCameraExperiment: string;
}

export enum DLViewEnum {
  AUTO_CAMERA,
  SUCCESSFUL_CAPTURE,
  INSTRUCTIONS,
  MANUAL_CAMERA,
  MANUAL_INSTRUCTIONS,
  MANUAL_REVIEW,
}

// declare var ZXing: any;

export class DocCapture extends React.Component<DocCaptureProps, DocCaptureState> {
  private compIsMounted: boolean;
  private errorCount = 0;

  constructor(props: DocCaptureProps) {
    super(props);
    PubSubEvents.subscribeLocalDecodeStarted(this.onLocalDecodeStarted);

    this.state = {
      docIndex: 0,
      enableLocalDecode: props.enableLocalDecode,
      firstUpload: true,
      view: DLViewEnum.INSTRUCTIONS,
      manualCameraExperiment: DOC_CAPTURE_EXPERIMENT_CONTROL,
    };
  }

  public render() {
    const docType = this.getCurrentDocument();

    let mode;
    let onImageCaptured;
    let resolution = SevenTwenty;
    switch (this.state.view) {
      case DLViewEnum.AUTO_CAMERA:
        mode = CameraMode.AUTO;
        onImageCaptured = this.onAutoCapture;
        break;
      case DLViewEnum.MANUAL_CAMERA:
        mode = CameraMode.MANUAL;
        onImageCaptured = this.onManualCapture;
        // only one manual upload so go big
        resolution = FourK;
        break;
      default:
        mode = CameraMode.OFF;
        onImageCaptured = () => { return; };
        break;
    }

    const {appId, uuid} = this.props;
    const id = appId ? appId : uuid;

    // we always have a camera element to prevent unmounting and init costs.
    const camera = (
      <Camera
        uploadId={id!}
        debug={this.props.debug}
        docType={docType}
        enableLocalDecode={this.state.enableLocalDecode}
        mode={mode}
        onExitClick={this.onCameraExit}
        onImageCaptured={onImageCaptured}
        resolution={resolution}
      />
    );

    const capturedImage = this.state.capturedImage;
    let display;
    switch (this.state.view) {
      case DLViewEnum.INSTRUCTIONS:
        display = (
          <CaptureInstructions
            currentIndex={this.state.docIndex}
            docType={docType}
            gotItClick={this.onInstructionsExit}
            numberOfDocs={this.props.documents.length}
          />
        );
        break;
      case DLViewEnum.MANUAL_INSTRUCTIONS:
        PubSubEvents.setPauseVideoActivities(true);
        display = (
          <ManualInstructions docType={docType} onDone={this.onManualInstructionsDone}/>
        );
        break;
      case DLViewEnum.MANUAL_REVIEW:
        if (!capturedImage) {
          display = <Marbles/>;
          break;
        }
        display = (
          <ImageReview
            image={capturedImage.image!}
            acceptImage={this.onImageReviewAccept}
            rejectImage={this.onImageReviewDecline}
          />
        );
        break;
      case DLViewEnum.SUCCESSFUL_CAPTURE:
        display = <SuccessfulCapture image={capturedImage ? capturedImage.image : ""}/>;
        break;
      default: display = (null);
    }

    let debug = (null);
    if (this.props.debug && this.state.backendAttempts != null) {
      debug = <h1 className={"DocCapture-debug"}>{"Backend Attempst: " + this.state.backendAttempts}</h1>;
    }

    return (
      <Fragment>
        {camera}
        {display}
        {debug}
      </Fragment>
    );
  }

  public componentWillUnmount() {
    this.compIsMounted = false;
    PubSub.unsubscribe(this.onLocalDecodeStarted);
  }

  public async componentDidMount() {
    this.compIsMounted = true;
    const manualCameraExperiment = await getVariation(this.props.uuid, this.props.appId,
        DOC_CAPTURE_MANUAL_CAMERA_EXPERIMENT);
    this.setState({manualCameraExperiment});
    setDeviceTags(this.props.uuid, this.props.appId, this.props.coBorrower, this.props.docType,
        IDVMethod.IN_HOUSE, IdUploadStatus.STARTED, navigator.mediaDevices.enumerateDevices()).catch((error) => {
          Logger.error("Error setting device tags for In-house OCR", error);
        }
    );
  }

  private onAutoCapture = (capturedImage: CapturedImage) => {
    if (!this.compIsMounted || this.state.view !== DLViewEnum.AUTO_CAMERA) {
      return;
    }

    this.setState({
      capturedImage,
    });

    this.validateImage(false, capturedImage);
  };

  private onCameraExit = () => {
    Routes.goToLanding(this.props.uuid, this.props.appId, this.props.docType);
  };

  private onInstructionsExit = () => {
    const { manualCameraExperiment } = this.state;

    this.setState({
      view: manualCameraExperiment === DOC_CAPTURE_EXPERIMENT_VARIATION ?
          DLViewEnum.MANUAL_CAMERA : DLViewEnum.AUTO_CAMERA,
    });

    PubSubEvents.resetForNewImage();
  };

  private onImageReviewAccept = () => {
    if (!this.state.capturedImage) {
      return;
    }
    this.validateImage(true, this.state.capturedImage);
    this.setState({
      view: DLViewEnum.SUCCESSFUL_CAPTURE,
    });
  };

  private onImageReviewDecline = () => {
    this.setState({
      capturedImage: undefined,
      view: DLViewEnum.MANUAL_CAMERA,
    });
  };

  private onLocalDecodeStarted = () => {
    ApiCalls.localDecodeStarted(this.props.uuid, this.props.appId, this.getCurrentDocument(), this.props.coBorrower);
  };

  private onManualCapture = (capturedImage: CapturedImage) => {
    if (this.state.view !== DLViewEnum.MANUAL_CAMERA) {
      return;
    }

    this.setState({
      capturedImage,
      view: DLViewEnum.MANUAL_REVIEW,
    });
  };

  private onManualInstructionsDone = () => {
    this.setState({
      view: DLViewEnum.MANUAL_CAMERA,
    });
  };

  private getCurrentDocument = (): DocType => {
    return this.props.documents[this.state.docIndex];
  };

  private captureAuto = (): void => {
    if (this.state.view !== DLViewEnum.AUTO_CAMERA) {
      return;
    }
    PubSubEvents.captureImage(this.getCurrentDocument() === DocType.LICENSE_BACK);
  };

  private validateImage = (forceUpload: boolean, capturedImage: CapturedImage): void => {
    let resetAttempts = false;
    if (this.state.firstUpload) {
      resetAttempts = true;
      this.setState({
        firstUpload: false,
      });
    }

    switch (capturedImage.result) {
      case CapturedImageResult.CAPTURED:

        let localDecodeAttempts: number | undefined;
        let decodeOffset: number | undefined;
        if (this.getCurrentDocument() === DocType.LICENSE_BACK) {
          localDecodeAttempts = capturedImage.localDecodeAttempts;
          decodeOffset = capturedImage.decodeOffset;
        }

        ApiCalls.validateDocument(
          this.props.uuid,
          this.props.appId,
          this.props.coBorrower,
          resetAttempts,
          this.getCurrentDocument(),
          capturedImage.image!,
          forceUpload,
          localDecodeAttempts,
          decodeOffset,
          (response) => {
            if (!this.compIsMounted) {
              return;
            }

            if (forceUpload || response.valid) {
              trackOptimizelyEvent(this.props.uuid, this.props.appId, DOC_CAPTURE_SUCCESS_EVENT);
              this.handleGoodImage(capturedImage);
              this.errorCount = 0;
              this.setState({
                backendAttempts: undefined,
              });
            } else {
              let attempts = 0;
              if (this.state.backendAttempts != null) {
                attempts = this.state.backendAttempts;
              }
              this.setState({
                backendAttempts: attempts + 1,
              });
              this.captureAuto();
            }
          },
          (error, data) => {
            if (!this.compIsMounted) {
              return;
            }
            this.setState({
              backendAttempts: undefined,
            });
            switch (error) {
              case ApiErrors.ALREADY_VALID: {
                this.handleGoodImage(capturedImage);
                break;
              }
              case ApiErrors.DOC_TYPE_NOT_FOUND: {
                this.handleGoodImage(capturedImage);
                break;
              }
              case ApiErrors.LOGGED_OUT: {
                PubSubEvents.setFlowState(FlowStates.LOGGED_OUT);
                break;
              }
              case ApiErrors.NO_ATTEMPTS_REMAINING: {
                this.setState({
                  view: DLViewEnum.MANUAL_INSTRUCTIONS,
                });
                break;
              }
              case ApiErrors.ANON_ID_EXPIRED: {
                Routes.expiredLinkLoginRedirect(data.appId, data.coborrower);
                break;
              }
              case ApiErrors.ANON_ID_NOT_FOUND: {
                PubSubEvents.setFlowState(FlowStates.UUID_NOT_FOUND);
                break;
              }
              default: {
                if (this.errorCount >= 5) {
                  Logger.error(`Encountered error validating an image: ${error}`);
                  PubSubEvents.setFlowState(FlowStates.MARBLES);
                } else {
                  this.errorCount++;
                  if (forceUpload) {
                    // just retry upload if forcing
                    this.validateImage(forceUpload, capturedImage);
                  } else {
                    this.captureAuto();
                  }
                }
              }
            }
          },
            [`docCaptureManualCamera:${this.state.manualCameraExperiment}`,
            `appId:${this.props.appId}`],
        );
        break;
      case CapturedImageResult.FAILED_DECODE_TIMEOUT:
        this.setState({
          view: DLViewEnum.MANUAL_INSTRUCTIONS,
        });
        break;
      default:
        Logger.error("An error occurred when trying to capture image");
    }
  };

  private handleGoodImage = (capturedImage: CapturedImage) => {
    this.setState({
      capturedImage,
      firstUpload: true,
      view: DLViewEnum.SUCCESSFUL_CAPTURE,
    });
    setTimeout(this.afterSuccess, 2000);
  };

  private afterSuccess = () => {
    const documents = this.props.documents;
    const docIndex = this.state.docIndex;

    if (docIndex >= documents.length - 1) {
      trackOptimizelyEvent(this.props.uuid, this.props.appId, DOC_CAPTURE_LOCAL_DECODE_SUCCESS_EVENT);
      setIdUploadStatus(this.props.uuid, this.props.appId, this.props.coBorrower, this.props.docType,
          IdUploadStatus.FINISHED).catch((err) => {
        Logger.error("Failed to set id upload status to finished from Manual Upload.", err);
      });
      Routes.goToFinished();
      return;
    }

    this.setState({
      capturedImage: undefined,
      docIndex: docIndex + 1,
      view: DLViewEnum.INSTRUCTIONS,
    });
  };
}



// WEBPACK FOOTER //
// ./src/components/doc-capture/DocCapture.tsx